import { FC } from 'react';

import Box from '@material-ui/core/Box';
import ButtonBase from '@material-ui/core/ButtonBase';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';

import type { ExerciseAnswer } from 'api/rest/exercises/exercises.types';

import { ON_GO_COLORS } from '../onGoColors';
import { groupTrueFalseAnswers } from './groupExerciseAnswers';
import OnGoChoiceLabel from './OnGoChoiceLabel';

const useStyles = makeStyles(theme => ({
  groups: {
    display: 'grid',
    gap: theme.spacing(1.5),
  },
  groupRow: {
    display: 'grid',
    gridTemplateColumns: 'auto 1fr 1fr',
    alignItems: 'stretch',
    gap: theme.spacing(1),
  },
  groupIndex: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 28,
    fontWeight: 600,
    color: theme.palette.text.secondary,
  },
  choice: {
    width: '100%',
    justifyContent: 'center',
    textAlign: 'center',
    padding: theme.spacing(1.75, 2),
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
    textTransform: 'capitalize',
    transition: 'border-color 120ms ease, background-color 120ms ease',
  },
  selectedChoice: {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.action.selected,
  },
  correctChoice: {
    border: `2px solid ${ON_GO_COLORS.correct}`,
    backgroundColor: ON_GO_COLORS.correctSoft,
  },
  incorrectChoice: {
    border: `2px solid ${ON_GO_COLORS.incorrect}`,
    backgroundColor: ON_GO_COLORS.incorrectSoft,
  },
  mutedChoice: {
    opacity: 0.7,
  },
}));

type OnGoTrueFalseChoicesProps = {
  answers: ExerciseAnswer[];
  disabled?: boolean;
  isResultPhase: boolean;
  isAttemptCorrect: boolean | null;
  onSelect: (answerId: number, group: number) => void;
  correctAnswerIdSet: Set<number>;
  selectedAnswerIds: number[];
};

const OnGoTrueFalseChoices: FC<OnGoTrueFalseChoicesProps> = ({
  answers,
  disabled = false,
  isResultPhase,
  isAttemptCorrect,
  onSelect,
  correctAnswerIdSet,
  selectedAnswerIds,
}) => {
  const classes = useStyles();
  const groups = groupTrueFalseAnswers(answers);
  const selectedByGroup = new Map<number, number>();

  for (const answerId of selectedAnswerIds) {
    const answer = answers.find(item => item.id === answerId);
    if (answer?.group != null) {
      selectedByGroup.set(answer.group, answerId);
    }
  }

  const getChoiceClassName = (answer: ExerciseAnswer, isSelected: boolean) => {
    const isCorrectOption = correctAnswerIdSet.has(answer.id);

    if (!isResultPhase) {
      return clsx(classes.choice, isSelected && classes.selectedChoice);
    }

    if (isCorrectOption) {
      return clsx(classes.choice, classes.correctChoice);
    }
    if (isSelected && isAttemptCorrect === false) {
      return clsx(classes.choice, classes.incorrectChoice);
    }
    return clsx(classes.choice, classes.mutedChoice);
  };

  return (
    <Box className={classes.groups}>
      {groups.map(({ group, choices }, index) => (
        <Box className={classes.groupRow} key={group}>
          <Typography className={classes.groupIndex} variant='body2'>
            {index + 1}.
          </Typography>
          {choices.map(answer => {
            const isSelected = selectedByGroup.get(group) === answer.id;
            return (
              <ButtonBase
                key={answer.id}
                aria-pressed={!isResultPhase ? isSelected : undefined}
                className={getChoiceClassName(answer, isSelected)}
                disabled={disabled || isResultPhase}
                onClick={() => onSelect(answer.id, group)}
              >
                <OnGoChoiceLabel label={answer.label} />
              </ButtonBase>
            );
          })}
        </Box>
      ))}
    </Box>
  );
};

export default OnGoTrueFalseChoices;
